// Per-dealer product visibility helpers (frontend-only, hide-list model).
//
// `hidden` is the dealer's `hidden_items` object from their Firestore doc:
//   { products: number[], variationItems: number[], detailItems: number[], detailDetailItems: number[] }
// Anything NOT in these lists is visible. Empty/missing lists => everything visible,
// so dealers without any visibility settings are unaffected.

// Normalize a possibly-missing hidden_items object into a Set lookup per level.
function toSets(hidden) {
  const h = hidden || {};
  return {
    subcategories: new Set((h.subcategories || []).map(Number)),
    products: new Set((h.products || []).map(Number)),
    variationItems: new Set((h.variationItems || []).map(Number)),
    detailItems: new Set((h.detailItems || []).map(Number)),
    detailDetailItems: new Set((h.detailDetailItems || []).map(Number)),
  };
}

// Remove hidden subcategories from the category browse grid.
export function filterSubcategoryList(subcategories, hidden) {
  if (!Array.isArray(subcategories)) return subcategories;
  const sets = toSets(hidden);
  if (sets.subcategories.size === 0) return subcategories;
  return subcategories.filter((s) => !sets.subcategories.has(Number(s.id)));
}

// Remove hidden products from a store product list (the category browse list).
export function filterProductList(products, hidden) {
  if (!Array.isArray(products)) return products;
  const sets = toSets(hidden);
  if (sets.products.size === 0) return products;
  return products.filter((p) => !sets.products.has(Number(p.id)));
}

// Return a copy of the product-details tree with hidden options pruned at every level.
// Tree shape (from /products/{id}/details):
//   product.variations[] { id, items[] { id, details[] { detailItems[] { detailDetails[] { detailDetailItems[] } } } } }
// Hiding a node naturally removes its entire subtree because we filter the parent's array.
export function pruneProductTree(product, hidden) {
  if (!product || !Array.isArray(product.variations)) return product;
  const sets = toSets(hidden);

  const variations = product.variations.map((variation) => {
    const items = (variation.items || [])
      .filter((item) => !sets.variationItems.has(Number(item.id)))
      .map((item) => {
        const details = (item.details || []).map((detail) => {
          const detailItems = (detail.detailItems || [])
            .filter((di) => !sets.detailItems.has(Number(di.id)))
            .map((di) => {
              const detailDetails = (di.detailDetails || []).map((dd) => ({
                ...dd,
                detailDetailItems: (dd.detailDetailItems || []).filter(
                  (ddi) => !sets.detailDetailItems.has(Number(ddi.id))
                ),
              }));
              return { ...di, detailDetails };
            });
          return { ...detail, detailItems };
        });
        return { ...item, details };
      });
    return { ...variation, items };
  });

  return { ...product, variations };
}

// True if pruning has left a REQUIRED variation with no selectable items —
// that product would be unorderable for the dealer. Used to warn the admin.
export function hasUnorderableRequiredVariation(prunedProduct) {
  if (!prunedProduct || !Array.isArray(prunedProduct.variations)) return false;
  return prunedProduct.variations.some(
    (v) => v.required && (!v.items || v.items.length === 0)
  );
}
